import _ from 'lodash';
import Vue from 'vue';

export default {
    placesAutoCompleteService: null,
    placesService: null,
    googleNotLoadedError: { message: 'There was an error loading google services.' },
    errors: {
        googleNotLoaded: {
            message: 'LS FE: Google not available on window / loaded',
            sessionCount: 0,
        },
        autoCompleteServiceNotCreated: {
            message: 'LS FE: Google autoCompleteService not created',
            sessionCount: 0,
        },
        placesServiceNotCreated: {
            message: 'LS FE: Google placesService not created',
            sessionCount: 0,
        },
        predictionErrorStatus: {
            message: 'LS FE: Google getPlacePredictions status not OK/ZERO_RESULTS',
            sessionCount: 0,
        },
        placeDetailsErrorStatus: {
            message: 'LS FE: Google placeDetails status not OK/ZERO_RESULTS',
            sessionCount: 0,
        },
    },

    /**
     * Initializes Places Autocomplete Service and Places Service if not initialized already
     */
    init() {
        return new Promise((resolve, reject) => {
            Vue.$gmapApiPromiseLazy().then(() => {
                const { google } = window;
                if (google) {
                    if (!this.placesService && _.get(google, 'maps.places.PlacesService')) {
                        const placesHelperNode = document.getElementById('ls-google-services-node');
                        if (placesHelperNode) {
                            this.placesService = new google.maps.places.PlacesService(
                                placesHelperNode
                            );
                        }
                    }
                    if (
                        !this.placesAutoCompleteService &&
                        _.get(google, 'maps.places.AutocompleteService')
                    ) {
                        this.placesAutoCompleteService = new google.maps.places.AutocompleteService();
                    }
                    resolve();
                } else {
                    this.errorHandler({
                        error: this.errors.googleNotLoaded,
                        reject,
                    });
                }
            });
        });
    },

    /**
     * Get place predictions from Places Autocomplete Service
     * @param {Object} config                           - See google api docs for allowed config options
     * @param {string} config.input                     - String to use for search
     * @param {Object} config.componentRestrictions     - See google api docs for allowed options
     * @param {string[]} config.types                   - See google api docs for allowed types
     * @return {Promise}
     */
    getPlacePredictions(config) {
        return this.init().then(
            () =>
                new Promise((resolve, reject) => {
                    if (this.placesAutoCompleteService) {
                        this.placesAutoCompleteService.getPlacePredictions(
                            {
                                input: config.input,
                                componentRestrictions: config.componentRestrictions || {
                                    country: 'us',
                                },
                                types: config.types || ['address'],
                            },
                            (predictions, status) => {
                                if (status === 'OK' || status === 'ZERO_RESULTS') {
                                    const predictionResponse = _.isArray(predictions)
                                        ? predictions
                                        : [];
                                    resolve(predictionResponse);
                                } else {
                                    this.errorHandler({
                                        error: this.errors.predictionErrorStatus,
                                        data: { status },
                                        reject,
                                    });
                                }
                            }
                        );
                    } else {
                        this.errorHandler({
                            error: this.errors.autoCompleteServiceNotCreated,
                            reject,
                        });
                    }
                })
        );
    },

    /**
     * Get place details using Places Service
     * @param {Object} config                           - See google api docs for allowed config options
     * @param {string} config.placeId                   - placeId available from prediction.place_id
     * @return {Promise}
     */
    getDetails(config) {
        return this.init().then(
            () =>
                new Promise((resolve, reject) => {
                    if (this.placesService) {
                        this.placesService.getDetails(
                            {
                                ...config,
                                fields: [
                                    'address_component',
                                    'formatted_address',
                                    'geometry',
                                    'name', // may not be needed
                                    'place_id',
                                    'type', // may not be needed
                                ],
                            },
                            (placeWithDetails, status) => {
                                if (status === 'OK' || status === 'ZERO_RESULTS') {
                                    resolve(placeWithDetails);
                                } else {
                                    this.errorHandler({
                                        error: this.errors.placeDetailsErrorStatus,
                                        data: { status },
                                        reject,
                                    });
                                }
                            }
                        );
                    } else {
                        this.errorHandler({
                            error: this.errors.placesServiceNotCreated,
                            reject,
                        });
                    }
                })
        );
    },

    /**
     * Get the first prediction based off an address string
     * @param {string} address                          - Address string
     * @return {Promise<any>}
     */
    getDetailsFromFirstPrediction(address) {
        return new Promise((resolve, reject) => {
            this.getPlacePredictions({
                // trim any empty spaces, and strip off any ending ', USA' or ', United States' from address
                input: _.replace(_.trim(address), /(,\sUSA$)|(,\sUnited\sStates$)/, ''),
                componentRestrictions: { country: 'us' },
                types: ['address'],
            })
                .then((predictions) => {
                    const placeId = _.get(_.head(predictions), 'place_id');

                    this.getDetails({
                        placeId,
                    })
                        .then((details) => {
                            resolve(details);
                        })
                        .catch((error) => {
                            reject(error);
                        });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    incrementErrorSessionCount({ error }) {
        // eslint-disable-next-line no-param-reassign
        error.sessionCount = _.add(error.sessionCount, 1);
    },

    errorHandler({ error, data, reject }) {
        this.incrementErrorSessionCount({ error });

        reject(_.assign({}, error, data));
    },
};
